import React from 'react';
import { motion } from 'framer-motion';
import styles from './css/Home.module.css';

const SERVICES = [
  {
    icon: '🌐',
    title: 'Web Development',
    desc: 'Modern, responsive web applications built with React.js. From concept to deployment — fast, scalable, and future-proof.',
    items: ['Single Page Applications (SPAs)', 'Progressive Web Apps (PWAs)', 'E-commerce Platforms', 'Custom Dashboards & Admin Panels'],
  },
  {
    icon: '📱',
    title: 'Mobile Development',
    desc: 'Native-quality mobile apps for iOS & Android using React Native. One codebase, seamless performance across both platforms.',
    items: ['Cross-Platform Mobile Apps', 'iOS & Android Development', 'App Store Deployment', 'Mobile UI/UX Optimisation'],
  },
  {
    icon: '⚙️',
    title: 'Full-Stack Solutions',
    desc: 'End-to-end development from database to deployment. Robust backends and elegant frontends working seamlessly together.',
    items: ['RESTful & GraphQL APIs', 'Database Design & Optimisation', 'Authentication & Security', 'Cloud Deployment (AWS, Azure, GCP)'],
  },
  {
    icon: '🔗',
    title: 'Software Integration',
    desc: 'Connecting your existing systems and third-party services. Streamline workflows with custom integrations and automation.',
    items: ['API Integration & Development', 'Third-Party Service Integration', 'Legacy System Modernisation', 'Workflow Automation'],
  },
];

const TECH_STACK = [
  { title: 'Frontend',  items: ['React.js', 'React Native', 'TypeScript', 'Next.js'] },
  { title: 'Backend',   items: ['Node.js', 'Express', 'Python', '.NET'] },
  { title: 'Database',  items: ['PostgreSQL', 'MongoDB', 'MySQL', 'Redis'] },
  { title: 'DevOps',    items: ['Docker', 'AWS', 'CI/CD', 'Git'] },
];

const fadeUp = {
  hidden:  { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0 },
};

function Home() {
  return (
    <div className={styles.page}>

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <motion.div
          className={styles.heroInner}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.8 }}
        >
          <h1 className={styles.heroTitle}>
            Building Digital Solutions<br />
            <span>That Scale</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Full-stack development expertise delivering <strong>React.js</strong>,{' '}
            <strong>React Native</strong>, and enterprise-grade software solutions —
            from concept to deployment.
          </p>
          <div className={styles.heroCtas}>
            <a href="#services" className={styles.ctaPrimary}>Explore Services</a>
            <a href="#contact" className={styles.ctaSecondary}>Get in Touch</a>
          </div>
        </motion.div>
      </section>

      {/* ── Services ── */}
      <section id="services" className={styles.section}>
        <motion.div
          className={styles.sectionHeader}
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className={styles.sectionLabel}>What We Build</span>
          <div className={styles.divider} />
          <h2 className={styles.sectionTitle}>End-to-End Solutions</h2>
          <p className={styles.sectionSubtitle}>
            From mobile apps to enterprise platforms — quality at every layer of the stack.
          </p>
        </motion.div>

        <div className={styles.serviceGrid}>
          {SERVICES.map((s, i) => (
            <motion.div
              key={i}
              className={styles.serviceCard}
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              viewport={{ once: true, amount: 0.15 }}
            >
              <span className={styles.serviceIcon}>{s.icon}</span>
              <h3 className={styles.serviceTitle}>{s.title}</h3>
              <p className={styles.serviceDesc}>{s.desc}</p>
              <ul className={styles.serviceList}>
                {s.items.map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Tech Stack ── */}
      <section className={styles.techSection}>
        <motion.div
          className={styles.sectionHeader}
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className={styles.sectionLabel}>Our Stack</span>
          <div className={styles.divider} />
          <h2 className={styles.sectionTitle}>Technology Stack</h2>
          <p className={styles.sectionSubtitle}>
            Modern, battle-tested technologies chosen for performance, scalability, and longevity.
          </p>
        </motion.div>

        <motion.div
          className={styles.techGrid}
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.6, delay: 0.15 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          {TECH_STACK.map((group, i) => (
            <div key={i}>
              <h4 className={styles.techGroupTitle}>{group.title}</h4>
              <div className={styles.techTags}>
                {group.items.map((item, j) => (
                  <span key={j} className={styles.techTag}>{item}</span>
                ))}
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ── Contact CTA ── */}
      <section id="contact" className={styles.contactSection}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <h2 className={styles.contactTitle}>Ready to Build Something Great?</h2>
          <p className={styles.contactSubtitle}>
            Let's discuss your project and turn your ideas into reality.
          </p>
          <a href="mailto:contact@byte79.com" className={styles.ctaPrimary}>
            Get in Touch →
          </a>
        </motion.div>
      </section>

      {/* ── Ticker ── */}
      <div className={styles.ticker}>
        <div className={styles.tickerTrack}>
          {/* <img src={require('./assets/Byte79_02.png')} height={16} alt="Byte79" className={styles.tickerLogo} /> */}
          <span>Full&#8209;Stack Web &amp; Mobile Development Solutions</span>
          <span className={styles.tickerSep}>◆</span>
          <span>React.js &nbsp;·&nbsp; React Native &nbsp;·&nbsp; Software Integration</span>
          <span className={styles.tickerSep}>◆</span>
          {/* <img src={require('./assets/Byte79_02.png')} height={16} alt="Byte79" className={styles.tickerLogo} /> */}
          <span>Full&#8209;Stack Web &amp; Mobile Development Solutions</span>
          <span className={styles.tickerSep}>◆</span>
          <span>React.js &nbsp;·&nbsp; React Native &nbsp;·&nbsp; Software Integration</span>
          <span className={styles.tickerSep}>◆</span>
        </div>
      </div>

    </div>
  );
}

export default Home;

